// src/components/SingleCell/SingleCellV3Page.jsx
import React, { useEffect, useMemo, useState } from "react";

function fmt(v, digits = 4) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(digits);
}

function fmt2(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(2);
}

function metricValue(row, keys = []) {
  for (const k of keys) {
    const n = Number(row?.[k]);
    if (Number.isFinite(n)) return n;
  }
  return NaN;
}

function fmtMetric(row, keys = []) {
  const n = metricValue(row, keys);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(4);
}

function csvEscape(v) {
  const s = String(v ?? "");
  if (/[",\n\t]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function modelLabelShort(name) {
  const x = String(name || "").toLowerCase();
  if (x === "logistic_regression" || x === "logreg") return "LR";
  if (x === "random_forest") return "RF";
  if (x === "decision_tree") return "DT";
  if (x === "svm_linear") return "SVM";
  if (x === "cnn") return "CNN";
  return String(name || "");
}

function modelLabelLong(name) {
  const x = String(name || "").toLowerCase();
  if (x === "logistic_regression" || x === "logreg") return "Logistic Regression";
  if (x === "random_forest") return "Random Forest";
  if (x === "decision_tree") return "Decision Tree";
  if (x === "svm_linear") return "Linear SVM";
  if (x === "cnn") return "CNN";
  return String(name || "");
}

function normalizeGroupKey(groupName = "") {
  const g = String(groupName).trim();
  if (!g) return "";
  return g.replace(/\s+/g, "_");
}

function groupSortKey(groupName = "") {
  const g = normalizeGroupKey(groupName);
  const order = ["Female_CD4", "Female_CD8", "Male_CD4", "Male_CD8"];
  const idx = order.indexOf(g);
  return idx >= 0 ? idx : 999;
}

function colorForGroup(groupName = "") {
  const g = normalizeGroupKey(groupName);
  if (g.startsWith("Female")) {
    if (g.includes("CD4")) return "#dc2626";
    if (g.includes("CD8")) return "#f87171";
    return "#ef4444";
  }
  if (g.startsWith("Male")) {
    if (g.includes("CD4")) return "#2563eb";
    if (g.includes("CD8")) return "#60a5fa";
    return "#3b82f6";
  }
  return "#6b7280";
}

function SectionHeader({ title, count, childrenRight }) {
  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        color: "#24324d",
        fontSize: 18,
        fontWeight: 600,
        marginBottom: 10,
      }}
    >
      <span>
        {title}
        {typeof count === "number" ? ` (${count})` : ""}
      </span>
      <span>{childrenRight}</span>
    </div>
  );
}

function GroupedMetricByModelChart({
  title,
  rows,
  metricKey,
  metricKeys,
  emptyText = "No chart data available.",
}) {
  const prepared = useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return null;

    const modelOrder = ["cnn", "decision_tree", "logistic_regression", "random_forest", "svm_linear"];
    const grouped = new Map();

    const keys = Array.isArray(metricKeys) && metricKeys.length ? metricKeys : [metricKey];

    rows.forEach((r) => {
      const model = String(r?.model_name || "").trim();
      const group = normalizeGroupKey(r?.group_name || "");

      let metric = NaN;
      for (const k of keys) {
        const candidate = Number(r?.[k] ?? NaN);
        if (Number.isFinite(candidate)) {
          metric = candidate;
          break;
        }
      }

      if (!model || !Number.isFinite(metric)) return;
      if (!grouped.has(model)) grouped.set(model, {});
      grouped.get(model)[group] = metric;
    });

    const models = Array.from(grouped.keys()).sort((a, b) => {
      const ia = modelOrder.indexOf(a);
      const ib = modelOrder.indexOf(b);
      if (ia >= 0 && ib >= 0) return ia - ib;
      if (ia >= 0) return -1;
      if (ib >= 0) return 1;
      return a.localeCompare(b);
    });

    const series = ["Female_CD4", "Female_CD8", "Male_CD4", "Male_CD8"];

    return { grouped, models, series };
  }, [rows, metricKey, metricKeys]);

  if (!prepared || !prepared.models.length) {
    return <div style={{ color: "#24324d", marginTop: 8 }}>{emptyText}</div>;
  }

  const { grouped, models, series } = prepared;

  const leftPad = 54;
  const rightPad = 20;
  const topPad = 26;
  const bottomPad = 78;
  const height = 360;
  const modelSlot = 180;
  const width = Math.max(820, leftPad + rightPad + models.length * modelSlot);
  const innerW = width - leftPad - rightPad;
  const innerH = height - topPad - bottomPad;
  const yMax = 1.0;

  const y = (v) => topPad + innerH - (Math.max(0, Math.min(yMax, v)) / yMax) * innerH;

  const groupBand = innerW / models.length;
  const clusterWidth = Math.min(120, groupBand * 0.7);
  const gap = 8;
  const barW = Math.max(16, Math.min(24, (clusterWidth - gap * 3) / 4));

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ color: "#24324d", marginBottom: 8, fontWeight: 600, textAlign: "center" }}>
        {title}
      </div>

      <div
        style={{
          overflowX: "auto",
          background: "#edf2ff",
          borderRadius: 10,
          padding: "10px 8px 14px 8px",
        }}
      >
        <svg width={width} height={height} role="img" aria-label={title}>
          <rect x="0" y="0" width={width} height={height} rx="10" fill="#edf2ff" />

          {[0, 0.25, 0.5, 0.75, 1.0].map((tick) => {
            const yy = y(tick);
            return (
              <g key={tick}>
                <line
                  x1={leftPad}
                  y1={yy}
                  x2={width - rightPad}
                  y2={yy}
                  stroke="#c9d2f0"
                  strokeWidth="1"
                />
                <text
                  x={leftPad - 8}
                  y={yy + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill="#4b5563"
                >
                  {tick.toFixed(2)}
                </text>
              </g>
            );
          })}

          <line
            x1={leftPad}
            y1={topPad}
            x2={leftPad}
            y2={topPad + innerH}
            stroke="#64748b"
            strokeWidth="1.2"
          />
          <line
            x1={leftPad}
            y1={topPad + innerH}
            x2={width - rightPad}
            y2={topPad + innerH}
            stroke="#64748b"
            strokeWidth="1.2"
          />

          {models.map((model, i) => {
            const modelCenter = leftPad + groupBand * i + groupBand / 2;
            const startX = modelCenter - clusterWidth / 2;

            return (
              <g key={model}>
                {series.map((seriesName, sIdx) => {
                  const value = grouped.get(model)?.[seriesName];
                  if (!Number.isFinite(value)) return null;

                  const barX = startX + sIdx * (barW + gap);
                  const barY = y(value);
                  const baseY = topPad + innerH;
                  const fill = colorForGroup(seriesName);

                  return (
                    <g key={`${model}-${seriesName}`}>
                      <rect
                        x={barX}
                        y={barY}
                        width={barW}
                        height={Math.max(0, baseY - barY)}
                        rx="3"
                        fill={fill}
                      />
                      <text
                        x={barX + barW / 2}
                        y={barY - 6}
                        textAnchor="middle"
                        fontSize="10"
                        fill={fill}
                        fontWeight="600"
                      >
                        {fmt2(value)}
                      </text>
                    </g>
                  );
                })}

                <text
                  x={modelCenter}
                  y={topPad + innerH + 20}
                  textAnchor="middle"
                  fontSize="12"
                  fill="#24324d"
                  fontWeight="600"
                >
                  {modelLabelShort(model)}
                </text>
              </g>
            );
          })}

          <g transform={`translate(${leftPad}, ${height - 26})`}>
            {["Female_CD4", "Female_CD8", "Male_CD4", "Male_CD8"].map((name, idx) => (
              <g key={name} transform={`translate(${idx * 150}, 0)`}>
                <rect x="0" y="-10" width="12" height="12" rx="2" fill={colorForGroup(name)} />
                <text x="18" y="0" fontSize="12" fill="#24324d">
                  {name.replace("_", " ")}
                </text>
              </g>
            ))}
          </g>
        </svg>
      </div>
    </div>
  );
}

export default function SingleCellV3Page() {
  const params = new URLSearchParams(window.location.search);
  const method = params.get("method") || "random_forest";
  const ts = params.get("ts") || String(Date.now());
  const isAllModels = method === "all_models";

  const [manifest, setManifest] = useState(null);
  const [allModelsData, setAllModelsData] = useState(null);

  const [selectedModel, setSelectedModel] = useState("logreg");
  const [gender, setGender] = useState("All");
  const [cellType, setCellType] = useState("");
  const [topK, setTopK] = useState(20);

  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [showBestTable, setShowBestTable] = useState(false);
  const [showAllSummaryTable, setShowAllSummaryTable] = useState(false);

  const modelOptions = [
    { value: "logreg", label: "Logistic Regression" },
    { value: "random_forest", label: "Random Forest" },
    { value: "decision_tree", label: "Decision Tree" },
    { value: "svm_linear", label: "Linear SVM" },
    { value: "cnn", label: "CNN" },
  ];

  const biomarkerMethod = isAllModels ? selectedModel : method;

  useEffect(() => {
    let cancelled = false;

    async function loadAllModelsSummary() {
      try {
        setBusy(true);
        setError("");
        setInfo("");
        setRows([]);
        setManifest(null);
        setAllModelsData(null);

        const r = await fetch(
          `/api/single-cell/all-models/summary?ts=${encodeURIComponent(ts)}`,
          { cache: "no-store" }
        );
        const j = await r.json().catch(() => ({}));

        if (!r.ok) {
          throw new Error(j?.error || `Failed to load all-model summary: HTTP ${r.status}`);
        }

        if (!cancelled) {
          const bestRows = Array.isArray(j?.best_rows) ? j.best_rows : [];
          const summaryRows = Array.isArray(j?.summary_rows) ? j.summary_rows : [];

          setAllModelsData({
            best_rows: bestRows,
            summary_rows: summaryRows,
            best_csv: j?.best_csv || "",
            summary_csv: j?.summary_csv || "",
          });
          setInfo("Choose Gender and Cell type, then click Load.");
        }
      } catch (e) {
        if (!cancelled) setError(String(e?.message || e));
      } finally {
        if (!cancelled) setBusy(false);
      }
    }

    if (isAllModels) {
      loadAllModelsSummary();
    } else {
      setAllModelsData(null);
    }

    return () => {
      cancelled = true;
    };
  }, [isAllModels, ts]);

  useEffect(() => {
    let cancelled = false;

    async function loadManifest() {
      try {
        setBusy(true);
        setError("");
        setRows([]);
        setManifest(null);

        const manifestUrl = isAllModels
          ? `/api/single-cell/all-models/biomarkers/manifest?selected_method=${encodeURIComponent(
              selectedModel
            )}&ts=${encodeURIComponent(ts)}`
          : `/api/single-cell/biomarkers/manifest?method=${encodeURIComponent(
              method
            )}&ts=${encodeURIComponent(ts)}`;

        const r = await fetch(manifestUrl, { cache: "no-store" });
        const j = await r.json().catch(() => ({}));

        if (!r.ok) {
          throw new Error(j?.error || `Failed to load manifest: HTTP ${r.status}`);
        }

        const rawGroups = Array.isArray(j?.groups) ? j.groups : [];

        const normalizedGroups = rawGroups
          .map((g) => {
            const groupName = String(g?.group || "").trim();

            let gGender = String(g?.gender || "").trim();
            let gCellType = String(g?.cell_type || "").trim();

            if ((!gGender || !gCellType) && groupName) {
              const parts = groupName.split("_");
              if (parts.length >= 2) {
                gGender = gGender || parts[0];
                gCellType = gCellType || parts.slice(1).join("_");
              }
            }

            return {
              group: groupName,
              gender: gGender || "Unknown",
              cell_type: gCellType || groupName,
              folds: Array.isArray(g?.folds) ? g.folds : [],
              classes: Array.isArray(g?.classes) ? g.classes : ["all"],
              n_rows: Number(g?.n_rows || 0),
            };
          })
          .filter((g) => g.group);

        const genders = Array.from(
          new Set(normalizedGroups.map((g) => g.gender).filter(Boolean))
        ).sort();

        const cellTypes = Array.from(
          new Set(normalizedGroups.map((g) => g.cell_type).filter(Boolean))
        ).sort();

        if (!cancelled) {
          setManifest({
            method: biomarkerMethod,
            csv: j?.csv || "",
            groups: normalizedGroups,
            genders,
            cellTypes,
          });
          setGender("All");
          setCellType(cellTypes[0] || "");
          if (!cellTypes.length) {
            setInfo(`No biomarker rows are available yet for method '${biomarkerMethod}'.`);
          } else {
            setInfo("Choose Gender and Cell type, then click Load.");
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(String(e?.message || e));
          setManifest({
            method: biomarkerMethod,
            csv: "",
            groups: [],
            genders: [],
            cellTypes: [],
          });
          setCellType("");
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    }

    loadManifest();

    return () => {
      cancelled = true;
    };
  }, [isAllModels, method, selectedModel, biomarkerMethod, ts]);

  const availableGenders = useMemo(() => {
    if (!manifest?.groups?.length) return ["All"];
    return ["All", ...manifest.genders];
  }, [manifest]);

  const availableCellTypes = useMemo(() => {
    if (!manifest?.groups?.length) return [];

    if (gender === "All") {
      return Array.from(new Set(manifest.groups.map((g) => g.cell_type))).sort();
    }

    return Array.from(
      new Set(
        manifest.groups
          .filter((g) => g.gender === gender)
          .map((g) => g.cell_type)
      )
    ).sort();
  }, [manifest, gender]);

  useEffect(() => {
    if (!availableCellTypes.length) {
      setCellType("");
      setRows([]);
      return;
    }

    if (!cellType || !availableCellTypes.includes(cellType)) {
      setCellType(availableCellTypes[0]);
      setRows([]);
    }
  }, [availableCellTypes, cellType]);

  const selectedGroups = useMemo(() => {
    if (!manifest?.groups?.length || !cellType) return [];

    return manifest.groups.filter((g) => {
      const cellMatch = g.cell_type === cellType;
      const genderMatch = gender === "All" ? true : g.gender === gender;
      return cellMatch && genderMatch;
    });
  }, [manifest, gender, cellType]);

  async function loadRows() {
    try {
      setBusy(true);
      setError("");
      setInfo("");

      if (!cellType) {
        throw new Error("Please choose a Cell type.");
      }

      if (!selectedGroups.length) {
        throw new Error("No biomarker results found for the selected filters.");
      }

      let combined = [];

      for (const g of selectedGroups) {
        const url = new URL(
          isAllModels
            ? "/api/single-cell/all-models/biomarkers/top"
            : "/api/single-cell/biomarkers/top",
          window.location.origin
        );

        if (isAllModels) {
          url.searchParams.set("selected_method", selectedModel);
        } else {
          url.searchParams.set("method", method);
        }

        url.searchParams.set("group", g.group);
        url.searchParams.set("k", String(topK));
        url.searchParams.set("absval", "true");
        url.searchParams.set("ts", ts);

        const r = await fetch(url.toString(), { cache: "no-store" });
        const j = await r.json().catch(() => ({}));

        if (!r.ok) {
          throw new Error(j?.error || `Failed to load biomarkers for ${g.group}: HTTP ${r.status}`);
        }

        combined = combined.concat(Array.isArray(j?.rows) ? j.rows : []);
      }

      combined.sort(
        (a, b) =>
          Math.abs(Number(b?.coefficient ?? 0)) -
          Math.abs(Number(a?.coefficient ?? 0))
      );

      const finalRows = combined.slice(0, Number(topK));
      setRows(finalRows);

      if (!finalRows.length) {
        setInfo("No biomarker rows were returned for the selected filters.");
      }
    } catch (e) {
      setError(String(e?.message || e));
      setRows([]);
    } finally {
      setBusy(false);
    }
  }

  function downloadRows() {
    if (!rows.length) {
      setError("No rows to download. Please click Load first.");
      return;
    }

    const header = ["gene", "coefficient", "group", "fold", "class"];
    const content = [
      header.join("\t"),
      ...rows.map((r) =>
        [
          csvEscape(r?.gene),
          csvEscape(r?.coefficient),
          csvEscape(r?.group),
          csvEscape(r?.fold),
          csvEscape(r?.class),
        ].join("\t")
      ),
    ].join("\n");

    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `biomarkers_${biomarkerMethod}_${gender}_${cellType || "NA"}_top${topK}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function downloadAllModelsTable(kind = "best") {
    if (!allModelsData) return;
    const data = kind === "best" ? allModelsData.best_rows : allModelsData.summary_rows;
    if (!Array.isArray(data) || !data.length) return;

    const cols = Object.keys(data[0]);
    const lines = [
      cols.join("\t"),
      ...data.map((row) => cols.map((c) => csvEscape(row[c])).join("\t")),
    ].join("\n");

    const blob = new Blob([lines], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = kind === "best" ? "best_model_per_group.txt" : "all_groups_model_summary.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function onGeneratePlots() {
    try {
      setBusy(true);
      setError("");

      const newWin = window.open("", "_blank");
      if (!newWin || newWin.closed) {
        throw new Error("Popup was blocked. Please allow popups for this site.");
      }

      newWin.document.write(`
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8">
            <title>Generating plots…</title>
          </head>
          <body style="font-family:Arial,sans-serif;padding:24px;background:#0b0f19;color:#e5e7eb;">
            Generating plots…
          </body>
        </html>
      `);
      newWin.document.close();

      const form = new URLSearchParams();
      form.set("clear", "1");
      form.set("method", biomarkerMethod);
      form.set("top_k", String(topK));
      form.set("clazz", "both");

      const r = await fetch("/api/plots/generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
        body: form.toString(),
      });

      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(j?.error || `Failed to generate plots: HTTP ${r.status}`);
      }

      newWin.location.href = `/api/plots/index?refresh=1&method=${encodeURIComponent(
        biomarkerMethod
      )}&ts=${Date.now()}`;
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  const bestRowsSorted = useMemo(() => {
    const rows = Array.isArray(allModelsData?.best_rows) ? [...allModelsData.best_rows] : [];
    return rows.sort((a, b) => groupSortKey(a?.group_name) - groupSortKey(b?.group_name));
  }, [allModelsData]);

  const summaryRowsSorted = useMemo(() => {
    const rows = Array.isArray(allModelsData?.summary_rows) ? [...allModelsData.summary_rows] : [];
    return rows.sort((a, b) => {
      const modelCmp = modelLabelLong(a?.model_name).localeCompare(modelLabelLong(b?.model_name));
      if (modelCmp !== 0) return modelCmp;
      return groupSortKey(a?.group_name) - groupSortKey(b?.group_name);
    });
  }, [allModelsData]);

  return (
    <div className="container" style={{ maxWidth: 1100, margin: "0 auto" }}>
      <h2 style={{ marginTop: 12 }}>Single-Cell — Biomarkers &amp; Results</h2>

      <div style={{ color: "#999", marginBottom: 12 }}>
        Method: {String(method).replace(/_/g, " ")}
      </div>

      {error ? (
        <div
          style={{
            background: "#fff4e5",
            color: "#7a4b00",
            border: "1px solid #f2c97d",
            padding: 10,
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      ) : null}

      {info ? (
        <div
          style={{
            background: "#eef3ff",
            color: "#334",
            border: "1px solid #cdd8ff",
            padding: 10,
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          {info}
        </div>
      ) : null}

      {isAllModels && (
        <>
          <div
            className="card"
            style={{
              padding: 12,
              borderRadius: 12,
              background: "#a7b2d2",
              marginBottom: 16,
            }}
          >
            <SectionHeader
              title="Best model per group"
              count={bestRowsSorted.length}
              childrenRight={
                <button
                  type="button"
                  onClick={() => setShowBestTable((v) => !v)}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#24324d",
                    cursor: "pointer",
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  {showBestTable ? "Hide table" : "Show table"}
                </button>
              }
            />

            <GroupedMetricByModelChart
              title="Best-model F1 by model"
              rows={bestRowsSorted}
              metricKeys={["mean_val_f1", "mean_test_f1", "test_f1", "val_f1"]}
            />

            <GroupedMetricByModelChart
              title="Best-model AUC by model"
              rows={bestRowsSorted}
              metricKeys={["mean_val_auc", "mean_test_auc", "test_auc", "val_auc"]}
            />

            {showBestTable && (
              <>
                <div style={{ overflowX: "auto", marginTop: 14 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", color: "#24324d" }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", padding: 8 }}>group_name</th>
                        <th style={{ textAlign: "left", padding: 8 }}>model_name</th>
                        <th style={{ textAlign: "left", padding: 8 }}>F1</th>
                        <th style={{ textAlign: "left", padding: 8 }}>AUC</th>
                        <th style={{ textAlign: "left", padding: 8 }}>mean_test_f1</th>
                        <th style={{ textAlign: "left", padding: 8 }}>mean_test_auc</th>
                        <th style={{ textAlign: "left", padding: 8 }}>mean_test_accuracy</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bestRowsSorted.map((r, i) => (
                        <tr key={`${r?.group_name || "grp"}-${i}`}>
                          <td style={{ padding: 8 }}>{r?.group_name ?? ""}</td>
                          <td style={{ padding: 8 }}>{r?.model_name ?? ""}</td>
                          <td style={{ padding: 8 }}>{fmtMetric(r, ["mean_val_f1", "mean_test_f1", "test_f1", "val_f1"])}</td>
                          <td style={{ padding: 8 }}>{fmtMetric(r, ["mean_val_auc", "mean_test_auc", "test_auc", "val_auc"])}</td>
                          <td style={{ padding: 8 }}>{fmt(r?.mean_test_f1)}</td>
                          <td style={{ padding: 8 }}>{fmt(r?.mean_test_auc)}</td>
                          <td style={{ padding: 8 }}>{fmt(r?.mean_test_accuracy)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ marginTop: 12 }}>
                  <button onClick={() => downloadAllModelsTable("best")}>
                    Download best-model summary
                  </button>
                </div>
              </>
            )}
          </div>

          <div
            className="card"
            style={{
              padding: 12,
              borderRadius: 12,
              background: "#a7b2d2",
              marginBottom: 16,
            }}
          >
            <SectionHeader
              title="All group model summary"
              count={summaryRowsSorted.length}
              childrenRight={
                <button
                  type="button"
                  onClick={() => setShowAllSummaryTable((v) => !v)}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#24324d",
                    cursor: "pointer",
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  {showAllSummaryTable ? "Hide table" : "Show table"}
                </button>
              }
            />

            <GroupedMetricByModelChart
              title="All-model F1 by model"
              rows={summaryRowsSorted}
              metricKeys={["mean_val_f1", "mean_test_f1", "test_f1", "val_f1"]}
            />

            <GroupedMetricByModelChart
              title="All-model AUC by model"
              rows={summaryRowsSorted}
              metricKeys={["mean_val_auc", "mean_test_auc", "test_auc", "val_auc"]}
            />

            {showAllSummaryTable && (
              <>
                <div style={{ overflowX: "auto", marginTop: 14 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", color: "#24324d" }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", padding: 8 }}>group_name</th>
                        <th style={{ textAlign: "left", padding: 8 }}>model_name</th>
                        <th style={{ textAlign: "left", padding: 8 }}>F1</th>
                        <th style={{ textAlign: "left", padding: 8 }}>AUC</th>
                        <th style={{ textAlign: "left", padding: 8 }}>mean_test_f1</th>
                        <th style={{ textAlign: "left", padding: 8 }}>mean_test_auc</th>
                        <th style={{ textAlign: "left", padding: 8 }}>mean_test_accuracy</th>
                        <th style={{ textAlign: "left", padding: 8 }}>n_completed_folds</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryRowsSorted.map((r, i) => (
                        <tr key={`${r?.group_name || "grp"}-${r?.model_name || "mdl"}-${i}`}>
                          <td style={{ padding: 8 }}>{r?.group_name ?? ""}</td>
                          <td style={{ padding: 8 }}>{r?.model_name ?? ""}</td>
                          <td style={{ padding: 8 }}>{fmtMetric(r, ["mean_val_f1", "mean_test_f1", "test_f1", "val_f1"])}</td>
                          <td style={{ padding: 8 }}>{fmtMetric(r, ["mean_val_auc", "mean_test_auc", "test_auc", "val_auc"])}</td>
                          <td style={{ padding: 8 }}>{fmt(r?.mean_test_f1)}</td>
                          <td style={{ padding: 8 }}>{fmt(r?.mean_test_auc)}</td>
                          <td style={{ padding: 8 }}>{fmt(r?.mean_test_accuracy)}</td>
                          <td style={{ padding: 8 }}>{r?.n_completed_folds ?? ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ marginTop: 12 }}>
                  <button onClick={() => downloadAllModelsTable("summary")}>
                    Download full comparison table
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}

      <div
        className="card"
        style={{
          padding: 12,
          borderRadius: 12,
          background: "#6d80d8",
          marginBottom: 14,
        }}
      >
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          {isAllModels && (
            <>
              <label>ML method</label>
              <select
                value={selectedModel}
                onChange={(e) => {
                  setSelectedModel(e.target.value);
                  setRows([]);
                  setError("");
                  setInfo("Choose Gender and Cell type, then click Load.");
                }}
              >
                {modelOptions.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </>
          )}

          <label>Gender</label>
          <select
            value={gender}
            onChange={(e) => {
              setGender(e.target.value);
              setRows([]);
              setError("");
            }}
          >
            {availableGenders.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>

          <label>Cell type</label>
          <select
            value={cellType}
            onChange={(e) => {
              setCellType(e.target.value);
              setRows([]);
              setError("");
            }}
          >
            {availableCellTypes.length === 0 ? <option value="">(none available)</option> : null}
            {availableCellTypes.map((ct) => (
              <option key={ct} value={ct}>
                {ct}
              </option>
            ))}
          </select>

          <label>Top-K</label>
          <input
            type="range"
            min="10"
            max="100"
            step="5"
            value={topK}
            onChange={(e) => setTopK(Number(e.target.value))}
          />
          <span>{topK}</span>

          <button onClick={loadRows} disabled={busy || !cellType}>
            {busy ? "Loading..." : "Load"}
          </button>

          <button onClick={downloadRows} disabled={busy || rows.length === 0}>
            Download
          </button>
        </div>
      </div>

      <div
        className="card"
        style={{
          padding: 12,
          borderRadius: 12,
          background: "#a7b2d2",
          marginBottom: 16,
        }}
      >
        {rows.length === 0 ? (
          <div style={{ color: "#24324d" }}>
            No rows loaded yet. Choose <b>Gender</b> and <b>Cell type</b>, then click <b>Load</b>.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", color: "#24324d" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: 8 }}>gene</th>
                <th style={{ textAlign: "left", padding: 8 }}>coefficient</th>
                <th style={{ textAlign: "left", padding: 8 }}>group</th>
                <th style={{ textAlign: "left", padding: 8 }}>fold</th>
                <th style={{ textAlign: "left", padding: 8 }}>class</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r?.group || "g"}-${r?.gene || "x"}-${i}`}>
                  <td style={{ padding: 8 }}>{r?.gene ?? ""}</td>
                  <td style={{ padding: 8 }}>{r?.coefficient ?? ""}</td>
                  <td style={{ padding: 8 }}>{r?.group ?? ""}</td>
                  <td style={{ padding: 8 }}>{r?.fold ?? ""}</td>
                  <td style={{ padding: 8 }}>{r?.class ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div
        className="card"
        style={{ padding: 12, borderRadius: 12, background: "#121826" }}
      >
        <button onClick={onGeneratePlots} disabled={busy}>
          {busy ? "Generating…" : "Generate & View Plots"}
        </button>
      </div>
    </div>
  );
}